import { SortBy, SortCriterion } from '@coveo/headless-react/ssr-commerce';
import {
  LayoutContainer,
  RteWrapper,
  SortByModal,
  SortByPopupWrapper,
} from '@greystar/designsystem';
import { clampTotalEntries, useAnalytics, usePagination, useSort } from '@greystar/lib';
import React, { useEffect, useRef, useState } from 'react';
import { SearchSortProps } from '../../../component-props';

interface SortTab {
  id: number;
  label: string;
  isActive: boolean;
  sort: SortCriterion;
}

const formatSortFieldLabel = (field: { name: string; direction?: string; displayName?: string }) =>
  field?.displayName ?? `${field.name}`.trim();

const getSortLabel = (criterion: SortCriterion): string => {
  switch (criterion.by) {
    case SortBy.Relevance:
      return 'Relevance';
    case SortBy.Fields:
      return criterion.fields.map(formatSortFieldLabel)[0];
  }
};

const buildSortTabs = (options: SortCriterion[]): SortTab[] => {
  const sortTabs: SortTab[] = [];
  options.forEach((option: SortCriterion, index: number) => {
    if (option.by === SortBy.Fields) {
      sortTabs.push({
        id: index + 1,
        label: getSortLabel(option),
        isActive: false,
        sort: option,
      });
    }
  });
  return sortTabs;
};
/**
 * SearchSort component renders a sorting and filtering interface for search results.
 *
 * @param {SearchSortProps} props - The properties for the SearchSort component.
 * @returns {JSX.Element} The rendered SearchSort component.
 *
 * @component
 *
 * @example
 * // Example usage of SearchSort component
 * <SearchSort totalResults={100} location="New York" />
 *
 * @remarks
 * This component uses the `useSort` hook to manage sorting state and methods.
 * It displays available sorting options as tabs and allows users to select a sorting method.
 * If there are no available sorting options, it renders an empty fragment.
 *
 * @param {number} props.totalResults - The total number of search results.
 * @param {string} props.location - The location of the search results.
 *
 * @hook useSort - Custom hook to manage sorting state and methods.
 *
 * @function buildSortTabs - Function to build sorting tabs from available sorts.
 *
 * @interface SortTab
 * @property {number} id - The unique identifier for the sort tab.
 * @property {string} label - The label for the sort tab.
 * @property {boolean} isActive - Indicates if the sort tab is active.
 * @property {string} sort - The sorting method associated with the tab.
 *
 * @component QuickLinkTab
 * @param {Array<{icon: string, label: string}>} quickLinks - Array of quick link objects.
 *
 * @cssclass sort-function-row - The main container for the sorting and filtering interface.
 * @cssclass sort-function-row--content-wrapper - Wrapper for the content inside the main container.
 * @cssclass sort-function-row--results-info - Container for the results information.
 * @cssclass sort-function-row--resultstext - Container for the results text.
 * @cssclass sort-function-row--result-count - Class for the result count text.
 * @cssclass sort-function-row--sort-label - Class for the sort label text.
 * @cssclass sort-tab-container - Container for the sorting tabs.
 * @cssclass sort-tab-container--sort-tab-selector - Selector for the sorting tabs.
 * @cssclass sort-tab-container--tabs-wrapper - Wrapper for the sorting tabs.
 * @cssclass sort-function-tab - Class for each sorting tab.
 * @cssclass sort-function-tab--active - Class for the active sorting tab.
 * @cssclass sort-function-tab--inactive - Class for the inactive sorting tabs.
 * @cssclass sort-function-tab--sort-tab-label - Class for the sorting tab label.
 * @cssclass sort-function-tab--border - Class for the border between sorting tabs.
 */
export const SearchSort = (props: SearchSortProps): JSX.Element => {
  const { state, methods } = useSort();
  const { state: paginateState } = usePagination();
  const hasTrackedSortEntry = useRef(false);
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const popupRef = useRef<HTMLDivElement>(null);
  const analytics = useAnalytics();

  const totalCount = props.totalResults <= 39 ? props.totalResults : paginateState.totalEntries;
  const totalEntries = clampTotalEntries(
    paginateState.totalPages,
    paginateState.pageSize,
    props.isFilterApplied ? totalCount : paginateState.totalEntries
  );

  const pushDataLayerEvent = (
    event: string,
    additionalData: Partial<{
      action_type: string;
      state?: string;
      property_location?: string;
      property_id?: string;
      property_name?: string;
      module_name?: string;
      inpage_location?: string;
      click_text?: string;
      click_type?: string;
      destination_url?: string;
    }> = {}
  ) => {
    const eventData = {
      action_type: additionalData.action_type,
      state: searchValue.split(',')[1]?.trim() ?? '',
      property_location: '',
      property_id: '',
      property_name: '',
      module_name: 'sorting mechanism',
      inpage_location: 'top',
      search_tearm: searchValue,
      click_text: additionalData.click_text ?? '',
      click_type: 'internal',
      destination_url: '',
    };
    analytics.trackEvent(event, eventData);
  };

  const trackSortEntry = (clickText: string) => {
    pushDataLayerEvent('sorting_mechanism_entry', {
      action_type: 'entry',
      click_text: clickText,
    });
  };

  const trackSortSelection = (direction: string) => {
    pushDataLayerEvent('sorting_mechanism_selection', {
      action_type: `price_${direction}_to_${direction === 'low' ? 'high' : 'low'}`,
      click_text: `Price (${direction === 'low' ? 'low to high' : 'high to low'})`,
    });
  };

  const trackShowResults = () => {
    pushDataLayerEvent('sorting_mechanism_result', {
      action_type: 'show results',
      click_text: 'Show Results',
    });
  };

  const handleShowResults = () => {
    trackShowResults();
    props.closeSortBy();
  };

  const activeCheckedTab = state.availableSorts.length
    ? buildSortTabs(state.availableSorts).find((tab) => tab.id === activeTab)?.label
    : undefined;
  const activeTabLabel = activeCheckedTab ? `: ${activeCheckedTab}` : '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        props.showSortByPopUp
      ) {
        props.closeSortBy();
      }
    };

    const handleScroll = () => {
      if (props.showSortByPopUp) {
        props.closeSortBy();
      }
    };

    if (props.showSortByPopUp) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, { capture: true });

      if (!hasTrackedSortEntry.current) {
        trackSortEntry(`Sort by${activeTabLabel}`);
        hasTrackedSortEntry.current = true;
      }
    } else {
      hasTrackedSortEntry.current = false;
    }

    const el = document.querySelector('[data-testid="search-input"]');
    const input = el instanceof HTMLInputElement ? el : null;
    const val = input?.value ?? '';
    setSearchValue(val);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [props.showSortByPopUp, props.closeSortBy, activeTab]);

  if (state.availableSorts.length === 0) {
    return <></>;
  }

  const start = paginateState.page * paginateState.pageSize + 1;
  const end = Math.min((paginateState.page + 1) * paginateState.pageSize, totalEntries);

  const tabs = buildSortTabs(state.availableSorts);

  const handleSortChange = (tab: SortTab) => {
    methods?.sortBy(tab.sort);
    setActiveTab(tab.id);

    if (tab.sort.by === SortBy.Fields && tab.sort.fields.length > 0) {
      const direction = tab.sort.fields[0]?.direction;
      props.onSortChange(direction);

      if (direction === 'asc' || direction === 'desc') {
        trackSortSelection(direction === 'asc' ? 'low' : 'high');
      } else if (direction === 'ascending' || direction === 'descending') {
        trackSortSelection(direction === 'ascending' ? 'low' : 'high');
      }
    }
  };

  const handleClearSort = () => {
    setActiveTab(null);
    props.onSortChange(undefined);
    methods?.sortBy({ by: SortBy.Relevance });
    pushDataLayerEvent('sorting_mechanism_clear', {
      action_type: 'clear sort',
      click_text: 'clear',
    });
  };
  const hasSelection = activeTab !== null;

  const handleToggleViewSortBy = () => {
    const currentClickText = `Sort by ${activeTabLabel}`;
    if (!props.showSortByPopUp) {
      pushDataLayerEvent('sorting_mechanism_entry', {
        action_type: 'entry',
        click_text: currentClickText,
      });
    }
    props.toggleViewSortBy();
  };

  const handleCloseSortBy = () => {
    pushDataLayerEvent('sorting_mechanism_close', {
      action_type: 'close',
      click_text: 'Close',
    });
    props.closeSortBy();
  };

  return (
    <section className="sort-function-row" aria-label="Sort and filter options">
      <div className="sort-function-row--results-info">
        <LayoutContainer flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <RteWrapper>
            <div
              className={
                props.mapExpandedView
                  ? 'map-expanded-view sort-function-row--title'
                  : 'sort-function-row--title !items-end'
              }
            >
              <h1 className="!text-[15px] md:!text-[19px] lg:!text-[22px] !mb-0 !pr-[10px] relative bottom-[1px]">
                {props.location === 'Searching...'
                  ? 'Searching...'
                  : props.totalResults === 0
                  ? 'No results'
                  : props.isMapSearch
                  ? `Apartments:`
                  : `Apartments for rent in ${props.location}:`}
              </h1>

              {props.location !== 'Searching...' &&
                props.location !== 'undefined, undefined' &&
                totalEntries > 0 && (
                  <p
                    className="sort-function-row--result-count !mr-[10px]"
                    data-testid="result-count"
                  >
                    Showing results {start}–{end} of {totalEntries}
                  </p>
                )}
            </div>
          </RteWrapper>

          <div ref={popupRef}>
            <SortByPopupWrapper
              showPopup={props.showSortByPopUp}
              miniButtonProps={{
                label: `Sort by ${activeTabLabel}`,
                onClick: handleToggleViewSortBy,
                icon: {
                  name: props.viewSortBy ? 'ArrowSmallUp' : 'ArrowSmallDown',
                  position: 'right',
                },
              }}
              sortByPopupProps={{
                showResultPanel: true,
                title: 'Sort by',
                resultsControlPanelProps: {
                  clearButtonLabel: hasSelection ? 'Clear' : undefined,
                  CTAButtonLabel: 'Show results',
                  clearFilter: handleClearSort,
                  showResults: handleShowResults,
                },
                radioProps: {
                  radioname: 'ss',
                  options: tabs.map((tab) => ({
                    label: tab.label,
                    value: tab.id.toString(),
                    id: tab.id.toString(),
                    checked: tab.id === activeTab,
                  })),
                  inputProps: {
                    onClick: (e: React.MouseEvent<HTMLInputElement>) => {
                      const target = e.currentTarget as HTMLInputElement;
                      const tab = tabs.find((t) => t.id.toString() === target.value);
                      if (tab) {
                        handleSortChange(tab);
                      }
                    },
                  },
                },
              }}
            />
          </div>

          {props.showSortByModal && (
            <SortByModal
              onClose={handleCloseSortBy}
              modalTitle="Sort by"
              showResultPanel={true}
              radioProps={{
                radioname: 'ss',
                options: tabs.map((tab) => ({
                  label: tab.label,
                  value: tab.id.toString(),
                  id: tab.id.toString(),
                  checked: tab.id === activeTab,
                })),
                inputProps: {
                  onClick: (e: React.MouseEvent<HTMLInputElement>) => {
                    const target = e.currentTarget as HTMLInputElement;
                    const tab = tabs.find((t) => t.id.toString() === target.value);
                    if (tab) {
                      handleSortChange(tab);
                    }
                  },
                },
              }}
              resultsControlPanelProps={{
                clearButtonLabel: hasSelection ? 'Clear' : undefined,
                CTAButtonLabel: 'Show results',
                clearFilter: handleClearSort,
                showResults: handleShowResults,
              }}
            />
          )}
        </LayoutContainer>
      </div>
    </section>
  );
};
